export interface ChangelogEntry {
  version: string
  date: string
  title: string
  changes: string[]
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: 'v0.9.10',
    date: '2026-06-03',
    title: 'Rating Nudges',
    changes: [
      'A new banner nudges you to rate unrated games on your Played list — rate right from the banner, and dismiss it until something new gets finished',
    ],
  },
  {
    version: 'v0.9.9',
    date: '2026-06-01',
    title: 'On Deck',
    changes: [
      'Pin wishlist games as "On Deck" to line up what your group plays next — they stay on the wishlist but pin to the top',
      'Drag to reorder your On Deck queue, and see the count in the sidebar',
    ],
  },
  {
    version: 'v0.9.8',
    date: '2026-05-23',
    title: 'Common Library',
    changes: [
      'New Common Library tab: see every game owned across your group, filter by who needs to join, and triage straight into wishlist / playing / played',
    ],
  },
  {
    version: 'v0.9.7',
    date: '2026-05-19',
    title: 'Tune Your Recommendations',
    changes: [
      'Write group preferences in a new tuning panel to steer your recommendations',
    ],
  },
  {
    version: 'v0.9.6',
    date: '2026-05-17',
    title: 'Dismiss Recommendations & More Themes',
    changes: [
      'Dismiss recommendations with a reason, and manage the dismissed list later',
      'Six new themes: forest, sunset, mono, matrix, sakura, and solarized-light',
      'Theme preference now syncs across devices on your account',
    ],
  },
  {
    version: 'v0.9.5',
    date: '2026-05-15',
    title: 'Wishlist Recommendations & Played Polish',
    changes: [
      'Recommendations now highlight games that group members have already wishlisted on Steam, with avatars showing who wants each one',
      'Sort and filter the Played page — surface unrated games or group favorites at a glance',
      'Half-star ratings with a drag-to-scrub bar that pops out on hover for easier precision',
    ],
  },
  {
    version: 'v0.9.4',
    date: '2026-05-13',
    title: 'Sortable Wishlist & Layout Polish',
    changes: [
      'Wishlist columns are now sortable — click any column header to sort',
      'Various layout and scaling fixes across screen sizes',
    ],
  },
  {
    version: 'v0.9.3',
    date: '2026-05-11',
    title: 'Smarter Game Search',
    changes: [
      'Search results now show a badge when a game is already on your wishlist, playing, or played — no more guessing',
      'Move games directly between lists from the search modal with a "Move to …" button',
      'Add games directly to Played without going through Now Playing first',
    ],
  },
  {
    version: 'v0.9.2',
    date: '2026-05-10',
    title: 'Co-op Improvements',
    changes: [
      'Improved Co-op data fetching',
    ],
  },
  {
    version: 'v0.9.1',
    date: '2026-05-08',
    title: 'Game Times & Nicknames',
    changes: [
      'How Long to Beat time estimates on wishlist, recommendations, and game detail',
      'Friend nicknames — set a custom display name for each Steam friend',
      'Recommendations now factor in your group size and current sale prices',
      'Mobile wishlist: price moved above votes for easier reading',
    ],
  },
  {
    version: 'v0.9.0',
    date: '2026-05-06',
    title: 'Initial Release',
    changes: [
      'Group wishlists with Steam game search and voting',
      'Steam login via OpenID',
      'Real-time updates via WebSockets',
      'Co-op player count and mode badges via Co-Optimus',
      'Price tracking with historical lows via ITAD',
      'Mobile-responsive layout',
      'Now Playing and Played & Rated tracking',
      'LLM-powered game recommendations',
      'Steam library and friend management',
    ],
  },
]

export const LATEST_CHANGELOG_VERSION = CHANGELOG[0].version

export function getUnreadCount(lastSeen: string | null | undefined): number {
  if (!lastSeen) {
    // All entries except the initial release are "new" for first-time viewers
    return CHANGELOG.filter((e) => e.version !== 'v0.9.0').length
  }
  const idx = CHANGELOG.findIndex((e) => e.version === lastSeen)
  return idx === -1 ? CHANGELOG.length : idx
}

// CHANGELOG is ordered newest-first, so a lower index means a more recent version.
// Unknown versions are treated as oldest; null/undefined means "never seen".
export function pickMoreRecentSeen(
  a: string | null | undefined,
  b: string | null | undefined,
): string | null | undefined {
  if (!a) return b
  if (!b) return a
  const ai = CHANGELOG.findIndex((e) => e.version === a)
  const bi = CHANGELOG.findIndex((e) => e.version === b)
  const an = ai === -1 ? Number.POSITIVE_INFINITY : ai
  const bn = bi === -1 ? Number.POSITIVE_INFINITY : bi
  return an <= bn ? a : b
}
